import { useCallback, useEffect, useState } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';
import { centsToMajorNumber, parseMoneyToCents } from '@/lib/money';

export type PaymentChargeType =
  | 'contractual_fee'
  | 'success_fee'
  | 'sucumbency'
  | 'reimbursement'
  | 'other';

export type SucumbencyCalculationType =
  | 'percentage'
  | 'fixed';

export type SucumbencyBasisType =
  | 'case_value'
  | 'judgment_amount'
  | 'economic_benefit'
  | 'manual';

export interface PaymentInstallment {
  id: string;
  paymentId: string;
  installmentNumber: number;
  amount: number;
  amountCents: number;
  paidAmount: number | null;
  paidAmountCents: number | null;
  dueDate: string | null;
  paidAt: string | null;
  status: string;
  paymentMethod: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Payment {
  id: string;
  clientId: string;
  clientName: string | null;
  caseId: string | null;
  caseTitle: string | null;
  feeRuleId: string | null;
  feeRuleName: string | null;
  title: string;
  description: string | null;
  chargeType: PaymentChargeType;

  sucumbencyCalculationType:
    SucumbencyCalculationType | null;

  sucumbencyBasisType:
    SucumbencyBasisType | null;

  sucumbencyPercentage:
    number | null;

  sucumbencyBasisAmountCents:
    number | null;

  totalAmount: number;
  totalAmountCents: number;
  entryAmount: number | null;
  entryAmountCents: number | null;
  installmentCount: number | null;
  installmentAmount: number | null;
  installmentAmountCents: number | null;
  successFeePercentage: number | null;
  paidAmount: number | null;
  paidAmountCents: number | null;
  balanceAmount: number | null;
  balanceAmountCents: number | null;
  status: string;
  paymentMethod: string | null;
  contractDate: string | null;
  firstDueDate: string | null;
  dueDay: number | null;
  responsibleUser: string | null;
  notes: string | null;
  installments: PaymentInstallment[];
  createdAt: string;
  updatedAt: string;
}

export interface CreatePaymentInput {
  clientId: string;
  caseId?: string | null;
  feeRuleId?: string | null;
  title: string;
  description?: string | null;
  chargeType?: PaymentChargeType;

  sucumbencyCalculationType?:
    SucumbencyCalculationType | null;

  sucumbencyBasisType?:
    SucumbencyBasisType | null;

  sucumbencyPercentage?:
    number | null;

  sucumbencyBasisAmountCents?:
    number | null;

  totalAmount: number;
  totalAmountCents?: number;
  entryAmount?: number | null;
  entryAmountCents?: number | null;
  installmentCount?: number | null;
  installmentAmount?: number | null;
  installmentAmountCents?: number | null;
  successFeePercentage?: number | null;
  paidAmount?: number | null;
  paidAmountCents?: number | null;
  status?: string;
  paymentMethod?: string | null;
  contractDate?: string | null;
  firstDueDate?: string | null;
  dueDay?: number | null;
  responsibleUser?: string | null;
  notes?: string | null;
}

export type UpdatePaymentInput = CreatePaymentInput;

export interface UpdateInstallmentInput {
  amount?: number | null;
  amountCents?: number | null;
  paidAmount?: number | null;
  paidAmountCents?: number | null;
  dueDate?: string | null;
  paidAt?: string | null;
  status?: string;
  paymentMethod?: string | null;
  notes?: string | null;
}

export interface PaymentLoadFilters {
  status?: string;
  clientId?: string;
  caseId?: string;
}

type UsePaymentsOptions = {
  autoLoad?: boolean;
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const toNullableInteger = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const number = Number(String(value).replace(',', '.'));
  return Number.isInteger(number) ? number : null;
};

const normalizePaymentChargeType = (
  value: unknown
): PaymentChargeType => {
  const normalized =
    String(value ?? '')
      .trim()
      .toLowerCase();

  switch (normalized) {
    case 'success_fee':
    case 'sucumbency':
    case 'reimbursement':
    case 'other':
      return normalized;

    default:
      return 'contractual_fee';
  }
};

const sortPayments = (a: Payment, b: Payment) => {
  const aTime = Date.parse(a.createdAt || '');
  const bTime = Date.parse(b.createdAt || '');

  if (Number.isFinite(aTime) && Number.isFinite(bTime) && aTime !== bTime) {
    return bTime - aTime;
  }

  return a.title.localeCompare(b.title, 'pt-BR');
};

const resolveMoneyCents = (
  centsValue: unknown,
  majorValue: unknown,
  fallback = 0
): number => {
  const directCents = toNullableInteger(centsValue);

  if (
    directCents !== null &&
    Number.isSafeInteger(directCents)
  ) {
    return directCents;
  }

  return parseMoneyToCents(majorValue) ?? fallback;
};

const resolveNullableMoneyCents = (
  centsValue: unknown,
  majorValue: unknown
): number | null => {
  if (
    centsValue === null &&
    (majorValue === null || majorValue === undefined)
  ) {
    return null;
  }

  if (
    centsValue === undefined &&
    (majorValue === null || majorValue === undefined)
  ) {
    return null;
  }

  const directCents = toNullableInteger(centsValue);

  if (
    directCents !== null &&
    Number.isSafeInteger(directCents)
  ) {
    return directCents;
  }

  return parseMoneyToCents(majorValue);
};

const normalizeInstallment = (
  value: any
): PaymentInstallment => {
  const amountCents = resolveMoneyCents(
    value?.amountCents ?? value?.amount_cents,
    value?.amount,
    0
  );

  const paidAmountCents =
    resolveNullableMoneyCents(
      value?.paidAmountCents ??
        value?.paid_amount_cents,
      value?.paidAmount ??
        value?.paid_amount
    );

  return {
    id: String(value?.id ?? ''),
    paymentId: String(
      value?.paymentId ??
        value?.payment_id ??
        ''
    ),
    installmentNumber: Number(
      value?.installmentNumber ??
        value?.installment_number ??
        0
    ),

    amount: centsToMajorNumber(amountCents),
    amountCents,

    paidAmount:
      paidAmountCents === null
        ? null
        : centsToMajorNumber(paidAmountCents),

    paidAmountCents,

    dueDate: toNullableText(
      value?.dueDate ?? value?.due_date
    ),

    paidAt: toNullableText(
      value?.paidAt ?? value?.paid_at
    ),

    status:
      String(value?.status ?? 'pending').trim() ||
      'pending',

    paymentMethod: toNullableText(
      value?.paymentMethod ??
        value?.payment_method
    ),

    notes: toNullableText(value?.notes),

    createdAt:
      toNullableText(
        value?.createdAt ??
          value?.created_at
      ) ?? new Date().toISOString(),

    updatedAt:
      toNullableText(
        value?.updatedAt ??
          value?.updated_at
      ) ?? new Date().toISOString(),
  };
};

const normalizePayment = (
  value: any
): Payment => {
  const totalAmountCents =
    resolveMoneyCents(
      value?.totalAmountCents ??
        value?.total_amount_cents,
      value?.totalAmount ??
        value?.total_amount,
      0
    );

  const entryAmountCents =
    resolveNullableMoneyCents(
      value?.entryAmountCents ??
        value?.entry_amount_cents,
      value?.entryAmount ??
        value?.entry_amount
    );

  const installmentAmountCents =
    resolveNullableMoneyCents(
      value?.installmentAmountCents ??
        value?.installment_amount_cents,
      value?.installmentAmount ??
        value?.installment_amount
    );

  const paidAmountCents =
    resolveNullableMoneyCents(
      value?.paidAmountCents ??
        value?.paid_amount_cents,
      value?.paidAmount ??
        value?.paid_amount
    );

  const balanceAmountCents =
    resolveNullableMoneyCents(
      value?.balanceAmountCents ??
        value?.balance_amount_cents,
      value?.balanceAmount ??
        value?.balance_amount
    );

  return {
    id: String(value?.id ?? ''),

    clientId: String(
      value?.clientId ??
        value?.client_id ??
        ''
    ).trim(),

    clientName: toNullableText(
      value?.clientName ??
        value?.client_name
    ),

    caseId: toNullableText(
      value?.caseId ?? value?.case_id
    ),

    caseTitle: toNullableText(
      value?.caseTitle ??
        value?.case_title
    ),

    feeRuleId: toNullableText(
      value?.feeRuleId ??
        value?.fee_rule_id
    ),

    feeRuleName: toNullableText(
      value?.feeRuleName ??
        value?.fee_rule_name
    ),

    chargeType:
      normalizePaymentChargeType(
        value?.chargeType ??
          value?.charge_type
      ),

    sucumbencyCalculationType:
      (
        value?.sucumbencyCalculationType ??
        value?.sucumbency_calculation_type
      ) === 'percentage'
        ? 'percentage'
        : (
            value?.sucumbencyCalculationType ??
            value?.sucumbency_calculation_type
          ) === 'fixed'
          ? 'fixed'
          : null,

    sucumbencyBasisType:
      (
        [
          'case_value',
          'judgment_amount',
          'economic_benefit',
          'manual',
        ] as const
      ).includes(
        value?.sucumbencyBasisType ??
          value?.sucumbency_basis_type
      )
        ? (
            value?.sucumbencyBasisType ??
            value?.sucumbency_basis_type
          )
        : null,

    sucumbencyPercentage:
      toNullableNumber(
        value?.sucumbencyPercentage ??
          value?.sucumbency_percentage
      ),

    sucumbencyBasisAmountCents:
      toNullableInteger(
        value?.sucumbencyBasisAmountCents ??
          value?.sucumbency_basis_amount_cents
      ),

    title: String(
      value?.title ?? ''
    ).trim(),

    description:
      toNullableText(value?.description),

    totalAmount:
      centsToMajorNumber(totalAmountCents),

    totalAmountCents,

    entryAmount:
      entryAmountCents === null
        ? null
        : centsToMajorNumber(
            entryAmountCents
          ),

    entryAmountCents,

    installmentCount:
      toNullableInteger(
        value?.installmentCount ??
          value?.installment_count
      ),

    installmentAmount:
      installmentAmountCents === null
        ? null
        : centsToMajorNumber(
            installmentAmountCents
          ),

    installmentAmountCents,

    successFeePercentage:
      toNullableNumber(
        value?.successFeePercentage ??
          value?.success_fee_percentage
      ),

    paidAmount:
      paidAmountCents === null
        ? null
        : centsToMajorNumber(
            paidAmountCents
          ),

    paidAmountCents,

    balanceAmount:
      balanceAmountCents === null
        ? null
        : centsToMajorNumber(
            balanceAmountCents
          ),

    balanceAmountCents,

    status:
      String(
        value?.status ?? 'pending'
      ).trim() || 'pending',

    paymentMethod:
      toNullableText(
        value?.paymentMethod ??
          value?.payment_method
      ),

    contractDate:
      toNullableText(
        value?.contractDate ??
          value?.contract_date
      ),

    firstDueDate:
      toNullableText(
        value?.firstDueDate ??
          value?.first_due_date
      ),

    dueDay:
      toNullableInteger(
        value?.dueDay ??
          value?.due_day
      ),

    responsibleUser:
      toNullableText(
        value?.responsibleUser ??
          value?.responsible_user
      ),

    notes:
      toNullableText(value?.notes),

    installments:
      Array.isArray(value?.installments)
        ? value.installments.map(
            normalizeInstallment
          )
        : Array.isArray(
              value?.paymentInstallments
            )
          ? value.paymentInstallments.map(
              normalizeInstallment
            )
          : [],

    createdAt:
      toNullableText(
        value?.createdAt ??
          value?.created_at
      ) ?? new Date().toISOString(),

    updatedAt:
      toNullableText(
        value?.updatedAt ??
          value?.updated_at
      ) ?? new Date().toISOString(),
  };
};

const toCanonicalPaymentPayload = (
  input: CreatePaymentInput
) => {
  const totalAmountCents =
    Number.isSafeInteger(
      input.totalAmountCents
    )
      ? input.totalAmountCents!
      : parseMoneyToCents(
          input.totalAmount
        ) ?? 0;

  const entryAmountCents =
    input.entryAmountCents !== undefined &&
    input.entryAmountCents !== null &&
    Number.isSafeInteger(
      input.entryAmountCents
    )
      ? input.entryAmountCents
      : parseMoneyToCents(
          input.entryAmount ?? 0
        ) ?? 0;

  const paidAmountCents =
    input.paidAmountCents !== undefined &&
    input.paidAmountCents !== null &&
    Number.isSafeInteger(
      input.paidAmountCents
    )
      ? input.paidAmountCents
      : parseMoneyToCents(
          input.paidAmount ?? 0
        ) ?? 0;

  return {
    ...input,
    totalAmountCents,
    entryAmountCents,
    paidAmountCents,
  };
};

const toCanonicalInstallmentPayload = (
  input: UpdateInstallmentInput
) => {
  const payload = {
    ...input,
  };

  if (
    input.amountCents !== undefined &&
    input.amountCents !== null
  ) {
    payload.amountCents =
      input.amountCents;
  } else if (
    input.amount !== undefined &&
    input.amount !== null
  ) {
    payload.amountCents =
      parseMoneyToCents(input.amount);
  }

  if (
    input.paidAmountCents !== undefined &&
    input.paidAmountCents !== null
  ) {
    payload.paidAmountCents =
      input.paidAmountCents;
  } else if (
    input.paidAmount !== undefined &&
    input.paidAmount !== null
  ) {
    payload.paidAmountCents =
      parseMoneyToCents(
        input.paidAmount
      );
  }

  return payload;
};
export function usePayments(options: UsePaymentsOptions = {}) {
  const { autoLoad = true } = options;
  const [payments, setPayments] = useState<Payment[]>([]);
  const [isLoadingPayments, setIsLoadingPayments] = useState(true);
  const [paymentsError, setPaymentsError] = useState<string | null>(null);

  const loadPayments = useCallback(async (filters?: PaymentLoadFilters) => {
    setIsLoadingPayments(true);
    setPaymentsError(null);

    try {
      const searchParams = new URLSearchParams();

      if (filters?.status) {
        searchParams.set('status', filters.status);
      }

      if (filters?.clientId) {
        searchParams.set('clientId', filters.clientId);
      }

      if (filters?.caseId) {
        searchParams.set('caseId', filters.caseId);
      }

      const endpoint = searchParams.toString() ? `/api/payments?${searchParams.toString()}` : '/api/payments';
      const response = await fetchWithRetry(endpoint);
      const data = await response.json();

      if (!response.ok || !data.success || !Array.isArray(data.payments)) {
        throw new Error(data.error || `GET /api/payments falhou com status ${response.status}`);
      }

      setPayments(data.payments.map(normalizePayment).sort(sortPayments));
    } catch (error) {
      console.error('Erro ao carregar pagamentos reais. ', error);
      setPayments([]);
      setPaymentsError('Não foi possível carregar os dados reais. Tente novamente.');
    } finally {
      setIsLoadingPayments(false);
    }
  }, []);

  const createPayment = useCallback(async (input: CreatePaymentInput) => {
    const response = await fetch('/api/payments', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(
        toCanonicalPaymentPayload(input)
      ),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.payment) {
      throw new Error(data.error || `POST /api/payments falhou com status ${response.status}`);
    }

    const created = normalizePayment(data.payment);
    setPayments((prev) => [created, ...prev.filter((payment) => payment.id !== created.id)].sort(sortPayments));
    void loadPayments();
    return created;
  }, [loadPayments]);

  const updatePayment = useCallback(
    async (id: string, input: UpdatePaymentInput) => {
      const response = await fetch(`/api/payments/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          toCanonicalPaymentPayload(input)
        ),
      });

      const data = await response.json();

      if (!response.ok || !data.success || !data.payment) {
        throw new Error(data.error || `PUT /api/payments/${id} falhou com status ${response.status}`);
      }

      await loadPayments();
      return normalizePayment(data.payment);
    },
    [loadPayments]
  );

  const archivePayment = useCallback(
    async (id: string) => {
      const response = await fetch(`/api/payments/${id}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || `DELETE /api/payments/${id} falhou com status ${response.status}`);
      }

      await loadPayments();
      return true;
    },
    [loadPayments]
  );

  const updateInstallment = useCallback(
    async (id: string, input: UpdateInstallmentInput) => {
      const response = await fetch(`/api/payment-installments/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(
          toCanonicalInstallmentPayload(input)
        ),
      });

      const data = await response.json();

      if (!response.ok || !data.success || !data.installment) {
        throw new Error(data.error || `PUT /api/payment-installments/${id} falhou com status ${response.status}`);
      }

      await loadPayments();
      return normalizeInstallment(data.installment);
    },
    [loadPayments]
  );

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void loadPayments();
  }, [autoLoad, loadPayments]);

  return {
    payments,
    isLoadingPayments,
    paymentsError,
    loadPayments,
    createPayment,
    updatePayment,
    archivePayment,
    updateInstallment,
  };
}
